import React, { useState, useEffect } from 'react';
import MealCard from './MealCard';

const MealSection = ({ type, meals, expandedSections, toggleSection, likedMeals, onLikeMeal }) => {
  const isExpanded = expandedSections.includes(type);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    // Reset to page 1 only when the meals array changes (not when likedMeals changes)
    setCurrentPage(1);
  }, [meals.length]); // Only depend on meals.length instead of entire meals array

  useEffect(() => {
    // Show scroll button only when section expanded and scrolled down
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (meals.length === 0) return null;

  // Sort meals: Seasonal first, then essential, then classic (alphabetically within each)
  const sortedMeals = [...meals].sort((a, b) => {
    const seasonA = a.season?.toLowerCase() || '';
    const seasonB = b.season?.toLowerCase() || '';
    
    const isSeasonalA = seasonA.includes('spring') || seasonA.includes('summer') || 
                         seasonA.includes('fall') || seasonA.includes('winter');
    const isSeasonalB = seasonB.includes('spring') || seasonB.includes('summer') || 
                         seasonB.includes('fall') || seasonB.includes('winter');
    const isClassicA = seasonA.includes('classic');
    const isClassicB = seasonB.includes('classic');
    const isEssentialA = seasonA.includes('essential');
    const isEssentialB = seasonB.includes('essential');
    // Essential is lowest priority
    
    // Order: 1. Seasonal, 2. Essential 3. Classic
    if (isSeasonalA && !isSeasonalB) return -1;
    if (!isSeasonalA && isSeasonalB) return 1;
    if (isEssentialA && !isEssentialB) return -1;
    if (!isEssentialA && isEssentialB) return 1;
    if (isClassicA && !isClassicB) return -1;
    if (!isClassicA && isClassicB) return 1;
    
    // If in same category, sort alphabetically
    return a.name.localeCompare(b.name);
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedMeals.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedMeals.slice(indexOfFirstItem, indexOfLastItem);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = () => {
    const sectionElement = document.getElementById(`section-${type}`);
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div key={type} className="mb-12" id={`section-${type}`}>
      <button 
        onClick={() => toggleSection(type)}
        className="w-full text-left text-2xl font-bold mb-2 capitalize flex items-center justify-between hover:text-blue-600 transition-colors bg-white p-4 rounded-lg shadow-sm"
      >
        <div>
          {type}
          <span className="text-gray-500 text-sm ml-2">({meals.length} items)</span>
        </div>
        <svg
          className={`w-6 h-6 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isExpanded && (
        <div className="rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {currentItems.map((meal) => (
              <MealCard 
                key={meal.id}
                meal={meal} 
                isLiked={likedMeals.some(m => m.id === meal.id)}
                onLike={onLikeMeal}
              />
            ))}
          </div>
          
          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6 flex-wrap gap-2">
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => {
                    setCurrentPage(1);
                    setTimeout(scrollToSection, 0);
                  }}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded ${currentPage === 1 ? 'bg-gray-200 text-gray-500' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                >
                  First
                </button>
                <button
                  onClick={() => {
                    setCurrentPage(prev => Math.max(prev - 1, 1));
                    setTimeout(scrollToSection, 0);
                  }}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded ${currentPage === 1 ? 'bg-gray-200 text-gray-500' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                >
                  Prev
                </button>
                <span className="px-3 py-1">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => {
                    setCurrentPage(prev => Math.min(prev + 1, totalPages));
                    setTimeout(scrollToSection, 0);
                  }}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded ${currentPage === totalPages ? 'bg-gray-200 text-gray-500' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                >
                  Next
                </button>
                <button
                  onClick={() => {
                    setCurrentPage(totalPages);
                    setTimeout(scrollToSection, 0);
                  }}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded ${currentPage === totalPages ? 'bg-gray-200 text-gray-500' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                >
                  Last
                </button>
              </div>
              <div>
                <span className="text-sm text-gray-600">Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, sortedMeals.length)} of {sortedMeals.length} items</span>
              </div>
            </div>
          )}
          
          {/* Back to top button */}
          <button
            onClick={scrollToSection}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors inline-flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
            Back to Section Top
          </button>
        </div>
      )}
      
      {/* Fixed back to top button (visible when scrolled) */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-4 right-4 p-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50"
          aria-label="Scroll to top"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7 7 7" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default MealSection;